import React from "react"
import autobind from "class-autobind"
import { Dialog, FlatButton, TextField, IconButton, FontIcon, SelectField, MenuItem, Toggle } from "material-ui"
import FinddDatePicker from "../../common/FinddDatePicker"
import strings from "../../../localization/strings"
import { ISchedulePatternTemplate, IScheduleDocument, IScheduleConstraint, EScheduleConstraintType, IJobDocument, IClientDocument, IDate, IScheduleTemplate } from "../../../models"
import moment from "moment"
import * as utils from "../../../helpers/utils"
import * as timeHelper from "../../../helpers/timeHelper"
import * as scheduleHelper from "../../../helpers/scheduleHelper"
import _ from "lodash"
import { blueGrey500 } from "material-ui/styles/colors"
import * as api from "../../../api"
import AppStateStore from "../../../stores/AppStateStore"
import ScheduleConstraintsTable from "../constraints/ScheduleConstraintsTable"
import EditIcon from "material-ui/svg-icons/image/edit"
import ScheduleEditDialog from "../detail/ScheduleEditDialog"
import AccountStore from "../../../stores/AccountStore"
import ChooseHierarchyJobChip from "../../hierarchy/choose_hierarchy/ChooseHierarchyJobChip"

enum EScheduleCopyMode {
	PostedSchedule,
	SchedulePlanner,
	MasterSchedule
}
interface IProps {
	schedules: (IScheduleDocument | IScheduleTemplate)[]
	handleClose(): void
	job: IJobDocument
	client: IClientDocument

	// ScheduleTemplate page.
	handleCopyScheduleTemplates?(scheduleTemplates: IScheduleTemplate[]): void

	// Schedule List pages.(Posted, Planner, MasterSchedule, Client->Schedule and Job->Schedule pages)
	handleReloadPage?(): void
}
interface IState {
	schedules: (IScheduleDocument | IScheduleTemplate)[]
	name: string
	errorName: string
	startDate: IDate
	needCheckValidataionOnBackend: boolean
	validationErrors: IScheduleConstraint[]
	scheduleCopyMode: EScheduleCopyMode
	errorScheduleCopyMode: string
	masterScheduleActive: boolean
	masterScheduleInAdvanceDays: number
	errorMasterScheduleInAdvanceDays: string
	openScheduleEditDialog: boolean
	overrideHierarchy: boolean
	hierarchyId: string
	job: IJobDocument
}

export default class CopyScheduleDialog extends React.Component<IProps, IState> {
	private FinddDatePicker
	private isScheduleTemplate
	private commonHierarchyJobData
	constructor(props: IProps) {
		super(props)
		this.isScheduleTemplate = !props.schedules[0].hasOwnProperty("AccountId")
		const startDate = this.getStartDate(props.schedules)
		this.commonHierarchyJobData = scheduleHelper.getCommonHierarchyJobData(props.schedules)
		this.state = {
			schedules: _.cloneDeep(props.schedules).map(i => scheduleHelper.initScheduleSchema(startDate, i)),
			name: props.schedules[0].Name,
			errorName: "",
			startDate,
			needCheckValidataionOnBackend: true,
			validationErrors: [],
			scheduleCopyMode: this.isScheduleTemplate && (props.schedules[0] as IScheduleDocument).MasterSchedule ?
				EScheduleCopyMode.MasterSchedule :
				null,
			errorScheduleCopyMode: null,
			masterScheduleActive: this.isScheduleTemplate && (props.schedules[0] as IScheduleDocument).MasterSchedule ?
				(props.schedules[0] as IScheduleDocument).MasterScheduleActive :
				true,
			masterScheduleInAdvanceDays: this.isScheduleTemplate && (props.schedules[0] as IScheduleDocument).MasterSchedule ?
				(props.schedules[0] as IScheduleDocument).InAdvanceDays :
				1,
			errorMasterScheduleInAdvanceDays: "",
			openScheduleEditDialog: false,
			overrideHierarchy: false,
			hierarchyId: this.commonHierarchyJobData.hierarchyId,
			job: props.job
		}
		autobind(this)
	}

	componentDidMount() {
		this.getJob()
	}

	getJob() {
		!this.props.job && this.commonHierarchyJobData.jobId === AccountStore.DiffJobId ?
			this.setState({ job: AccountStore.DiffJob }) :
			api.getJob(this.commonHierarchyJobData.jobId).then(res =>
				this.setState({ job: res.data })
			)
	}

	getStartDate(schedules: (IScheduleDocument | IScheduleTemplate)[]) {
		let latestEndDate
		schedules.forEach(schedule => {
			if (!latestEndDate || moment(schedule.EndDate).isAfter(latestEndDate)) {
				latestEndDate = schedule.EndDate
			}
		})
		const nextDayOfLatestEndDate = moment(timeHelper.keepConvertUTCToLocal(latestEndDate)).add(1, "day").startOf("day")
		const startToday = moment().startOf("day")
		const startDate = startToday.isAfter(nextDayOfLatestEndDate) ? startToday : nextDayOfLatestEndDate
		return moment(startDate).format()
	}

	checkValidate() {
		let isValidate = true
		if (!this.state.name.trim()) {
			this.setState({ errorName: strings.This_field_is_required })
			isValidate = false
		}
		else {
			this.setState({ errorName: "" })
		}

		if (!this.isScheduleTemplate && this.state.scheduleCopyMode === null) {
			this.setState({ errorScheduleCopyMode: strings.This_field_is_required })
			isValidate = false
		}
		else {
			this.setState({ errorScheduleCopyMode: "" })
		}

		if (this.state.scheduleCopyMode === EScheduleCopyMode.MasterSchedule) {
			if (!this.state.masterScheduleInAdvanceDays) {
				this.setState({ errorMasterScheduleInAdvanceDays: strings.This_field_is_required })
				isValidate = false
			}
			else if (Number(this.state.masterScheduleInAdvanceDays) < 1) {
				this.setState({ errorMasterScheduleInAdvanceDays: strings.formatString(strings.This_field_must_be_greater_than_or_equal_to_x, 1) as string })
				isValidate = false
			}
			else {
				this.setState({ errorMasterScheduleInAdvanceDays: "" })
			}
		}

		return isValidate
	}

	handleSave() {
		if (!this.checkValidate()) return
		const schedules = _.cloneDeep(this.state.schedules)
		schedules[0].Name = this.state.name

		const hierarchyId = this.state.hierarchyId
		const jobId = this.state.job ? this.state.job.Id : null
		const hierarchyChanged = hierarchyId !== this.commonHierarchyJobData.hierarchyId
		const jobChanged = jobId !== this.commonHierarchyJobData.jobId
		if (this.state.overrideHierarchy && (hierarchyChanged || jobChanged)) {
			schedules.forEach(schedule => {
				if (hierarchyChanged && hierarchyId !== AccountStore.DiffHierarchyLevel) {
					schedule.HierarchyId = hierarchyId
				}
				if (jobChanged && jobId !== AccountStore.DiffJobId) {
					schedule.JobId = jobId
					schedule.JobName = this.state.job ? this.state.job.Name : null
				}

				const patternTemplates = this.isScheduleTemplate ?
					(schedule as IScheduleTemplate).PatternTemplates :
					(schedule as IScheduleDocument).Schedules
				const overriddenPatternTemplates = this.overridePatternTemplatesHierarchy(patternTemplates, hierarchyChanged, this.state.hierarchyId, jobChanged, this.state.job, this.props.client)
				if (this.isScheduleTemplate) {
					(schedule as IScheduleTemplate).PatternTemplates = overriddenPatternTemplates
				}
				else {
					(schedule as IScheduleDocument).Schedules = overriddenPatternTemplates
				}
			})
		}

		if (this.isScheduleTemplate) {
			const scheduleTemplates = schedules as IScheduleTemplate[]
			scheduleTemplates.length > 1 &&
				scheduleTemplates.forEach(i => i.Name = `${i.Name} [Copy of]`)
			this.props.handleCopyScheduleTemplates(scheduleTemplates)
		}
		else {
			const copyToMasterSchedule = this.state.scheduleCopyMode === EScheduleCopyMode.MasterSchedule
			const publish = this.state.scheduleCopyMode === EScheduleCopyMode.PostedSchedule
			schedules.forEach(schedule => {
				(schedule as IScheduleDocument).MasterSchedule = copyToMasterSchedule;
				(schedule as IScheduleDocument).MasterScheduleActive = copyToMasterSchedule && this.state.masterScheduleActive;
				(schedule as IScheduleDocument).InAdvanceDays = copyToMasterSchedule ? this.state.masterScheduleInAdvanceDays : 0
			})

			const startDate = moment(this.state.startDate).utc(true).format()
			this.state.needCheckValidataionOnBackend ?
				AppStateStore.setProgressSnackbarContent("loading",
					setTimeout(() => api.getValidationForCopySchedule(startDate, schedules).then(res => {
						this.setState({ needCheckValidataionOnBackend: false })
						const validationErrors: IScheduleConstraint[] = res.data
						if (validationErrors.length === 0) {
							this.props.handleClose()
							api.copySchedule(startDate, schedules, publish).then(() => {
								this.props.handleReloadPage &&
									this.props.handleReloadPage()
								AppStateStore.setProgressSnackbarContent(strings.Schedule_copied_successfully)
							})
						}
						else {
							AppStateStore.setProgressSnackbarContent("")
							this.setState({ validationErrors })
						}
					}), 1000)
				) :
				this.state.validationErrors.find(item => item.Type === EScheduleConstraintType.ScheduleNameInvalid) ?
					AppStateStore.setAlertDialogContent({ content: strings.Cannot_copy_schedule_with_duplicated_name }) :
					AppStateStore.setConfirmDialogContent({
						title: strings.Warning,
						content: strings.Are_you_sure_you_want_to_copy_schedule_once_unassign_shift_had_conflict,
						confirm: () => {
							AppStateStore.setProgressSnackbarContent("loading")
							this.props.handleClose()
							api.copySchedule(startDate, schedules, publish).then(() => {
								this.props.handleReloadPage &&
									this.props.handleReloadPage()
								AppStateStore.setProgressSnackbarContent(strings.Schedule_copied_successfully)
							})
						}
					})
		}
	}

	overridePatternTemplatesHierarchy(patternTemplates: ISchedulePatternTemplate[], hierarchyChanged: boolean, hierarchy: string, jobChanged: boolean, scheduleJob: IJobDocument, scheduleClient: IClientDocument): ISchedulePatternTemplate[] {
		patternTemplates.forEach(patternTemplate => {
			if (hierarchyChanged && hierarchy !== AccountStore.DiffHierarchyLevel) {
				patternTemplate.HierarchyId = hierarchy
			}
			patternTemplate.ShiftTemplates.forEach(st => {
				if (hierarchyChanged && hierarchy !== AccountStore.DiffHierarchyLevel) {
					st.HierarchyId = hierarchy
				}
				if (jobChanged && scheduleJob !== AccountStore.DiffJob) {
					st.JobId = scheduleJob ? scheduleJob.Id : null
					st.JobName = scheduleJob ? scheduleJob.Name : null
					st.PhaseId = null
					st.PhaseName = null
				}
				st.ClientId = scheduleClient ? scheduleClient.Id : null
				st.ClientName = scheduleClient ? scheduleClient.Name : null
			})
		})
		return patternTemplates
	}

	render() {
		return (
			<Dialog
				open
				autoScrollBodyContent
				title={
					<div style={{ display: "flex", justifyContent: "space-between" }}>
						<span>{this.isScheduleTemplate ? strings.Copy_Schedule_Template : strings.Copy_Schedule}</span>
						{!this.isScheduleTemplate && this.state.schedules.length === 1 &&
							<IconButton onClick={() => this.setState({ openScheduleEditDialog: true })}>
								<EditIcon />
							</IconButton>
						}
					</div>
				}
				titleStyle={{ paddingLeft: 35, paddingBottom: 10 }}
				actions={[
					<FlatButton
						data-intercom-target={"CopySchedule-Btn-CloseCopyScheduleDialog"}
						label={strings.CANCEL}
						secondary
						onClick={this.props.handleClose}
					/>,
					<FlatButton
						data-intercom-target={"CopySchedule-Btn-SaveCopySchedule"}
						label={strings.SAVE}
						primary
						onClick={this.handleSave}
					/>
				]}
			>
				<hr />
				<div style={{ paddingRight: 20, paddingLeft: 20 }}>
					<Toggle
						data-intercom-target={"CopyScheduleDialog-Toggle-OverrideHierarchy"}
						label={strings.Override_hierarchy}
						toggled={this.state.overrideHierarchy}
						style={{ width: 225, marginTop: 25 }}
						onToggle={(e, value) => this.setState({ overrideHierarchy: value })}
					/>
					{this.state.overrideHierarchy &&
						<div>
							<ChooseHierarchyJobChip
								readonlyJob={this.props.job ? true : false}
								hideJob={!AccountStore.getJobsEnabled()}
								hierarchyChipLabel={strings.HierarchyLevel}
								jobChipLabel={strings.Job}
								hierarchyItemId={this.state.hierarchyId}
								jobId={this.state.job ? this.state.job.Id : null}
								hieararchyChipMaxWidth={700}
								jobChipMaxWidth={700}
								handleSelectHierarchyJob={(hierarchyId: string, job: IJobDocument) =>
									this.setState({ hierarchyId, job })
								}
							/>
						</div>
					}
					<div style={{ display: "flex", justifyContent: "space-between" }}>
						{this.state.schedules.length === 1 &&
							<TextField
								data-intercom-target={"CopySchedule-Entry-Name"}
								floatingLabelText={strings.Name}
								value={this.state.name}
								onChange={(e, value) =>
									this.setState({
										name: value,
										needCheckValidataionOnBackend: true,
										validationErrors: []
									}, () =>
										this.checkValidate()
									)
								}
								errorText={this.state.errorName}
								errorStyle={{ position: "absolute", bottom: -10 }}
							/>
						}
						{!this.isScheduleTemplate &&
							<div style={{ position: "relative" }}>
								<FinddDatePicker
									data-intercom-target={"CopySchedule-Select-ScheduleStartDate"}
									ref={elem => this.FinddDatePicker = elem}
									floatingLabelText={strings.Schedule_Start_Date}
									value={moment(this.state.startDate).toDate()}
									formatDate={date => moment(date).format("ddd MMM. DD, YYYY")}
									onChange={(_, startDate: Date) => this.setState({
										startDate,
										schedules: this.state.schedules.map(i => scheduleHelper.initScheduleSchema(startDate, i)),
										needCheckValidataionOnBackend: true,
										validationErrors: []
									})}
									cancelLabel={<span style={{ color: blueGrey500 }}>{strings.Cancel}</span>}
									firstDayOfWeek={0}
								/>
								<IconButton
									data-intercom-target={"CopySchedule-Btn-OpenScheduleStartDatePicker"}
									onTouchTap={() => this.FinddDatePicker.DatePicker.openDialog()}
									tooltip={"Pick a date"}
									style={{ position: "absolute", left: 215, top: 20 }}
								>
									<FontIcon className="fa fa-calendar fa-lg" />
								</IconButton>
							</div>
						}
					</div>
					{!this.isScheduleTemplate &&
						<div>
							<SelectField
								data-intercom-target={"CopySchedule-Select-CopyScheduleTo"}
								floatingLabelText={strings.Copy_Schedule_to}
								value={this.state.scheduleCopyMode}
								onChange={(e, key, payload) =>
									this.setState({
										scheduleCopyMode: payload,
										needCheckValidataionOnBackend: true,
										validationErrors: []
									}, () =>
										this.checkValidate()
									)
								}
								errorText={this.state.errorScheduleCopyMode}
							>
								<MenuItem value={EScheduleCopyMode.PostedSchedule} primaryText={strings.Posted_Schedule} />
								<MenuItem value={EScheduleCopyMode.SchedulePlanner} primaryText={strings.Schedule_Planners} />
								<MenuItem value={EScheduleCopyMode.MasterSchedule} primaryText={strings.Master_Schedules} />
							</SelectField>
						</div>
					}
					{this.state.scheduleCopyMode === EScheduleCopyMode.MasterSchedule &&
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<Toggle
								data-intercom-target={"CopySchedule-Toggle-MasterScheduleActive"}
								label={strings.Master_Schedule_Active}
								toggled={this.state.masterScheduleActive}
								style={{ width: 225, marginTop: 30 }}
								onToggle={(e, value) => this.setState({ masterScheduleActive: value })}
							/>
							<TextField
								data-intercom-target={"CopySchedule-Toggle-PostScheduleInAdvanceDays"}
								floatingLabelText={strings.Post_Schedules_In_Advance_Days}
								value={this.state.masterScheduleInAdvanceDays}
								onChange={(e, value) => utils.validateInteger(value) && this.setState({ masterScheduleInAdvanceDays: value }, () => this.checkValidate())}
								errorText={this.state.errorMasterScheduleInAdvanceDays}
								errorStyle={{ position: "absolute", bottom: -10 }}
							/>
						</div>
					}
				</div>
				{this.state.validationErrors.length > 0 &&
					<div style={{ marginTop: 15 }}>
						<ScheduleConstraintsTable
							constraints={this.state.validationErrors}
						/>
					</div>
				}
				{!this.isScheduleTemplate && this.state.openScheduleEditDialog &&
					<ScheduleEditDialog
						schedule={this.state.schedules[0] as IScheduleDocument}
						handleSave={schedule => this.setState({ schedules: [schedule], openScheduleEditDialog: false })}
						handleClose={() => this.setState({ openScheduleEditDialog: false })}
						job={this.props.job}
						client={this.props.client}
					/>
				}
			</Dialog>
		)
	}
}